import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { Button, Badge, Input, Select } from "../ui";

// ============================================================
// TYPES
// ============================================================

interface CalendarEvent {
  id?: number;
  title: string;
  type?: string;
}

interface Annotation {
  id: number;
  note: string;
  annotation_type: "note" | "event" | "weather" | "promo";
  created_at?: string;
}

interface CalendarDay {
  date: string;
  total_sales: number | null;
  transaction_count: number | null;
  cash_sales: number | null;
  card_sales: number | null;
  weather_high: number | null;
  weather_low: number | null;
  weather_condition: string | null;
  notable_events: CalendarEvent[];
  annotations: Annotation[];
  is_today: boolean;
  is_weekend: boolean;
  is_big_day: boolean;
  day_of_week: number;
}

interface ComparisonData {
  same_date_history: Array<{ date: string; total_sales: number; transaction_count: number }>;
  current_day: CalendarDay | null;
  same_dow_history: Array<{ date: string; total_sales: number; transaction_count: number }>;
  day_info: { day_of_week: string; date: string };
}

// ============================================================
// HELPERS
// ============================================================

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatCurrency(n: number | null): string {
  if (n === null || n === undefined) return "";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getDayNumber(dateStr: string): number {
  return new Date(dateStr + "T12:00:00").getDate();
}

function isCurrentMonth(dateStr: string, year: number, month: number): boolean {
  const d = new Date(dateStr + "T12:00:00");
  return d.getFullYear() === year && d.getMonth() + 1 === month;
}

const annotationTypeColors: Record<string, string> = {
  note: "var(--rbw-blue)",
  event: "var(--rbw-gold)",
  weather: "var(--rbw-gray-dark)",
  promo: "var(--rbw-success)",
};

const annotationTypeBadgeVariant: Record<string, "default" | "gold" | "blue" | "green" | "danger" | "purple"> = {
  note: "blue",
  event: "gold",
  weather: "default",
  promo: "green",
};

// ============================================================
// CALENDAR CELL
// ============================================================

function CalendarCell({
  day,
  isCurrMonth,
  onClick,
  isSelected,
}: {
  day: CalendarDay;
  isCurrMonth: boolean;
  onClick: () => void;
  isSelected: boolean;
}) {
  const dayNum = getDayNumber(day.date);
  const hasSales = day.total_sales !== null && day.total_sales > 0;
  const hasAnnotations = day.annotations && day.annotations.length > 0;
  const hasEvents = day.notable_events && day.notable_events.length > 0;

  // Build cell background
  let bg = "var(--rbw-bg-card)";
  if (!isCurrMonth) {
    bg = "var(--rbw-bg)";
  } else if (day.is_big_day && hasSales) {
    bg = "#F6BE000A";
  } else if (day.is_weekend) {
    bg = "#E8E5E008";
  }

  return (
    <button
      onClick={onClick}
      className="relative text-left w-full transition-all"
      style={{
        background: bg,
        borderLeft: day.is_today ? "4px solid var(--rbw-gold)" : "none",
        paddingLeft: day.is_today ? "0" : "4px",
        minHeight: 120,
        outline: isSelected ? "2px solid var(--rbw-blue)" : "none",
        outlineOffset: -2,
        opacity: isCurrMonth ? 1 : 0.35,
        cursor: "pointer",
      }}
    >
      <div className="p-2 h-full flex flex-col">
        {/* Date number + annotation icon */}
        <div className="flex items-start justify-between">
          <span
            className="text-base font-bold leading-none"
            style={{ color: day.is_today ? "var(--rbw-gold-dark)" : "var(--rbw-navy)" }}
          >
            {dayNum}
          </span>
          {hasAnnotations && (
            <svg
              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="var(--rbw-blue)"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          )}
        </div>

        {/* Sales total in handwritten font */}
        {hasSales && (
          <div
            className="mt-1 leading-tight"
            style={{
              fontFamily: "'Caveat', cursive",
              fontSize: "1.35rem",
              fontWeight: 700,
              color: day.is_big_day ? "var(--rbw-gold-dark)" : "var(--rbw-navy)",
            }}
          >
            {formatCurrency(day.total_sales)}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Event badges */}
        {hasEvents && (
          <div className="flex flex-wrap gap-1 mt-1">
            {day.notable_events.slice(0, 2).map((evt, i) => (
              <span
                key={i}
                className="inline-block px-1.5 py-0.5 rounded-full text-[9px] font-semibold truncate"
                style={{
                  background: "var(--rbw-gold)",
                  color: "var(--rbw-navy)",
                  maxWidth: "100%",
                }}
              >
                {evt.title}
              </span>
            ))}
            {day.notable_events.length > 2 && (
              <span
                className="text-[9px] font-semibold"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                +{day.notable_events.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

// ============================================================
// DAY DETAIL PANEL
// ============================================================

function DayDetailPanel({
  day,
  comparison,
  onClose,
  onAnnotationAdded,
  onAnnotationDeleted,
}: {
  day: CalendarDay;
  comparison: ComparisonData | null;
  onClose: () => void;
  onAnnotationAdded: () => void;
  onAnnotationDeleted: () => void;
}) {
  const [newNote, setNewNote] = useState("");
  const [newType, setNewType] = useState<string>("note");
  const [saving, setSaving] = useState(false);
  const [editingSales, setEditingSales] = useState(false);
  const [salesInput, setSalesInput] = useState("");
  const [savingSales, setSavingSales] = useState(false);

  const dateObj = new Date(day.date + "T12:00:00");
  const dayOfWeek = DOW_LABELS[dateObj.getDay()];
  const formatted = dateObj.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  async function handleAddAnnotation() {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await api.post("/calendar/annotations", {
        date: day.date,
        note: newNote.trim(),
        annotation_type: newType,
      });
      setNewNote("");
      setNewType("note");
      onAnnotationAdded();
    } catch (err) {
      console.error("Failed to save annotation:", err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAnnotation(id: number) {
    try {
      await api.delete(`/calendar/annotations/${id}`);
      onAnnotationDeleted();
    } catch (err) {
      console.error("Failed to delete annotation:", err);
    }
  }

  return (
    <div
      className="h-full flex flex-col overflow-y-auto"
      style={{
        background: "var(--rbw-bg-card)",
        borderLeft: "1px solid var(--rbw-border)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--rbw-border)" }}
      >
        <div>
          <div className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
            {dayOfWeek}, {formatted}
          </div>
          {day.is_big_day && (
            <span
              className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
              style={{ background: "#F6BE0020", color: "var(--rbw-gold-dark)" }}
            >
              Big Day
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: "var(--rbw-gray-dark)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--rbw-navy)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--rbw-gray-dark)")}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Sales breakdown */}
        <div>
          <h3
            className="text-xs font-bold uppercase tracking-wider mb-3"
            style={{ color: "var(--rbw-gray-dark)" }}
          >
            Sales
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <SummaryBox label="Total" value={formatCurrency(day.total_sales)} />
            <SummaryBox label="Transactions" value={day.transaction_count?.toString() || "--"} />
            <SummaryBox label="Cash" value={formatCurrency(day.cash_sales)} />
            <SummaryBox label="Card" value={formatCurrency(day.card_sales)} />
          </div>

          {/* Edit / Enter daily sales total */}
          {editingSales ? (
            <div className="mt-3 flex gap-2 items-center">
              <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>$</span>
              <input
                type="number"
                step="0.01"
                value={salesInput}
                onChange={(e) => setSalesInput(e.target.value)}
                placeholder="Enter total sales"
                className="flex-1 px-3 py-1.5 rounded-lg border text-sm"
                style={{ borderColor: "var(--rbw-border)" }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (!salesInput) return;
                    setSavingSales(true);
                    api.post("/calendar/daily-sales", { date: day.date, total_sales: parseFloat(salesInput) })
                      .then(() => { setEditingSales(false); onAnnotationAdded(); })
                      .catch((err) => console.error("Failed to save:", err))
                      .finally(() => setSavingSales(false));
                  }
                  if (e.key === "Escape") setEditingSales(false);
                }}
              />
              <button
                disabled={savingSales || !salesInput}
                onClick={() => {
                  if (!salesInput) return;
                  setSavingSales(true);
                  api.post("/calendar/daily-sales", { date: day.date, total_sales: parseFloat(salesInput) })
                    .then(() => { setEditingSales(false); onAnnotationAdded(); })
                    .catch((err) => console.error("Failed to save:", err))
                    .finally(() => setSavingSales(false));
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {savingSales ? "..." : "Save"}
              </button>
              <button
                onClick={() => setEditingSales(false)}
                className="px-2 py-1.5 rounded-lg text-xs"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setEditingSales(true); setSalesInput(day.total_sales ? day.total_sales.toString() : ""); }}
              className="mt-2 text-xs font-medium px-2 py-1 rounded-lg transition-colors"
              style={{ color: "var(--rbw-blue)", background: "var(--rbw-blue)10" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rbw-blue)20")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--rbw-blue)10")}
            >
              {day.total_sales ? "Edit Sales Total" : "+ Enter Sales Total"}
            </button>
          )}
        </div>

        {/* Weather */}
        {day.weather_condition && (
          <div>
            <h3
              className="text-xs font-bold uppercase tracking-wider mb-2"
              style={{ color: "var(--rbw-gray-dark)" }}
            >
              Weather
            </h3>
            <div
              className="rounded-xl p-3 text-sm"
              style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
            >
              <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                {day.weather_condition}
              </span>
              {day.weather_high !== null && day.weather_low !== null && (
                <span style={{ color: "var(--rbw-gray-dark)" }}>
                  {" "} &mdash; {day.weather_high}&deg;/{day.weather_low}&deg;
                </span>
              )}
            </div>
          </div>
        )}

        {/* Events */}
        {day.notable_events.length > 0 && (
          <div>
            <h3
              className="text-xs font-bold uppercase tracking-wider mb-2"
              style={{ color: "var(--rbw-gray-dark)" }}
            >
              Events
            </h3>
            <div className="space-y-1.5">
              {day.notable_events.map((evt, i) => (
                <div
                  key={i}
                  className="rounded-lg px-3 py-2 text-sm font-medium"
                  style={{
                    background: "#F6BE0010",
                    border: "1px solid #F6BE0030",
                    color: "var(--rbw-navy)",
                  }}
                >
                  {evt.title}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Annotations */}
        <div>
          <h3
            className="text-xs font-bold uppercase tracking-wider mb-2"
            style={{ color: "var(--rbw-gray-dark)" }}
          >
            Notes & Annotations
          </h3>
          {day.annotations.length > 0 ? (
            <div className="space-y-2 mb-3">
              {day.annotations.map((ann) => (
                <div
                  key={ann.id}
                  className="flex items-start gap-2 rounded-lg px-3 py-2"
                  style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: annotationTypeColors[ann.annotation_type] || "var(--rbw-gray)" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm" style={{ color: "var(--rbw-navy)" }}>
                      {ann.note}
                    </div>
                    <Badge variant={annotationTypeBadgeVariant[ann.annotation_type] || "default"}>
                      {ann.annotation_type}
                    </Badge>
                  </div>
                  <button
                    onClick={() => handleDeleteAnnotation(ann.id)}
                    className="p-1 rounded transition-colors flex-shrink-0"
                    style={{ color: "var(--rbw-gray)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--rbw-danger)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--rbw-gray)")}
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              No annotations yet.
            </div>
          )}

          {/* Add annotation form */}
          <div className="space-y-2">
            <Input
              value={newNote}
              onChange={setNewNote}
              placeholder="Add a note..."
            />
            <div className="flex gap-2">
              <Select value={newType} onChange={setNewType}>
                <option value="note">Note</option>
                <option value="event">Event</option>
                <option value="weather">Weather</option>
                <option value="promo">Promo</option>
              </Select>
              <Button
                onClick={handleAddAnnotation}
                disabled={!newNote.trim() || saving}
                size="sm"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>

        {/* Year-over-year comparison */}
        {comparison && (
          <div>
            <h3
              className="text-xs font-bold uppercase tracking-wider mb-2"
              style={{ color: "var(--rbw-gray-dark)" }}
            >
              Year-over-Year
            </h3>
            {comparison.same_date_history.length > 0 ? (
              <div className="space-y-1.5">
                {comparison.same_date_history.map((h, i) => {
                  const hDate = new Date(h.date + "T12:00:00");
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg px-3 py-2 text-sm"
                      style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
                    >
                      <span style={{ color: "var(--rbw-gray-dark)" }}>
                        {hDate.getFullYear()}
                      </span>
                      <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                        {formatCurrency(h.total_sales)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                No historical data available.
              </div>
            )}

            {comparison.same_dow_history.length > 0 && (
              <div className="mt-3">
                <div
                  className="text-xs font-semibold mb-1.5"
                  style={{ color: "var(--rbw-gray-dark)" }}
                >
                  Same Day of Week
                </div>
                <div className="space-y-1.5">
                  {comparison.same_dow_history.slice(0, 4).map((h, i) => {
                    const hDate = new Date(h.date + "T12:00:00");
                    return (
                      <div
                        key={i}
                        className="flex items-center justify-between rounded-lg px-3 py-2 text-sm"
                        style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
                      >
                        <span style={{ color: "var(--rbw-gray-dark)" }}>
                          {hDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                        <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                          {formatCurrency(h.total_sales)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryBox({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--rbw-gray-dark)" }}>
        {label}
      </div>
      <div className="text-lg font-bold mt-0.5" style={{ color: "var(--rbw-navy)" }}>
        {value || "--"}
      </div>
    </div>
  );
}

// ============================================================
// MAIN CALENDAR DASHBOARD
// ============================================================

export default function CalendarDashboard() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/calendar/month/${year}/${month}`);
      setDays(res.data);
    } catch (err) {
      console.error("Failed to load calendar:", err);
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  // When a day is selected, fetch comparison data
  useEffect(() => {
    if (!selectedDay) return;
    let cancelled = false;
    setComparison(null);
    api
      .get(`/calendar/day/${selectedDay.date}/comparison`)
      .then((res) => {
        if (!cancelled) setComparison(res.data);
      })
      .catch((err) => console.error("Failed to load comparison:", err));
    return () => {
      cancelled = true;
    };
  }, [selectedDay]);

  function goToToday() {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  }

  function goPrev() {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  }

  function goNext() {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  }

  function handleDayClick(day: CalendarDay) {
    setSelectedDay(day);
    setPanelOpen(true);
  }

  function handleClosePanel() {
    setPanelOpen(false);
    setSelectedDay(null);
  }

  function handleAnnotationChange() {
    // Re-fetch the month to update annotation indicators, and refresh selected day
    fetchMonth().then(() => {
      if (selectedDay) {
        // Update selectedDay from the refreshed data
        api.get(`/calendar/month/${year}/${month}`).then((res) => {
          const updatedDays: CalendarDay[] = res.data;
          const updated = updatedDays.find((d) => d.date === selectedDay.date);
          if (updated) setSelectedDay(updated);
        });
      }
    });
  }

  // Build rows of 7 days
  const rows: CalendarDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    rows.push(days.slice(i, i + 7));
  }

  return (
    <div className="h-full flex flex-col" style={{ background: "var(--rbw-bg)" }}>
      {/* Month navigation header */}
      <div
        className="flex items-center justify-between px-8 py-5 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--rbw-border)" }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={goPrev}
            className="p-2 rounded-xl transition-colors"
            style={{ color: "var(--rbw-navy)", background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rbw-gray-light)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--rbw-bg-card)")}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1
            className="text-3xl font-bold tracking-tight select-none"
            style={{ color: "var(--rbw-navy)", minWidth: 260, textAlign: "center" }}
          >
            {MONTH_NAMES[month - 1]} {year}
          </h1>
          <button
            onClick={goNext}
            className="p-2 rounded-xl transition-colors"
            style={{ color: "var(--rbw-navy)", background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rbw-gray-light)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--rbw-bg-card)")}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <Button onClick={goToToday} variant="ghost" size="sm">
          Today
        </Button>
      </div>

      {/* Calendar body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Calendar grid area */}
        <div className="flex-1 flex flex-col overflow-hidden px-6 pb-4 pt-2">
          {/* Day-of-week header */}
          <div
            className="grid grid-cols-7 flex-shrink-0"
            style={{ borderBottom: "2px solid var(--rbw-navy)" }}
          >
            {DOW_LABELS.map((dow) => (
              <div
                key={dow}
                className="py-2 text-center text-xs font-bold uppercase tracking-widest"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                {dow}
              </div>
            ))}
          </div>

          {/* Grid rows */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-lg font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-lg font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
                  No data available
                </div>
              </div>
            ) : (
              <div className="grid grid-rows-auto" style={{ minHeight: "100%" }}>
                {rows.map((row, ri) => (
                  <div
                    key={ri}
                    className="grid grid-cols-7"
                    style={{ borderBottom: "1px solid var(--rbw-border)" }}
                  >
                    {row.map((day) => {
                      const isCurr = isCurrentMonth(day.date, year, month);
                      return (
                        <div
                          key={day.date}
                          style={{ borderRight: "1px solid var(--rbw-border)" }}
                        >
                          <CalendarCell
                            day={day}
                            isCurrMonth={isCurr}
                            onClick={() => handleDayClick(day)}
                            isSelected={selectedDay?.date === day.date}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Side panel */}
        <div
          className="flex-shrink-0 overflow-hidden transition-all duration-300"
          style={{
            width: panelOpen ? 380 : 0,
            opacity: panelOpen ? 1 : 0,
          }}
        >
          {panelOpen && selectedDay && (
            <DayDetailPanel
              day={selectedDay}
              comparison={comparison}
              onClose={handleClosePanel}
              onAnnotationAdded={handleAnnotationChange}
              onAnnotationDeleted={handleAnnotationChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}
